import { useEffect } from 'react'

import { useTypedDispatch, useTypedSelector } from '../../..'
import { pollSummaryAction } from '../../../actions/defectAssistant'
import {
  beginSummaryFallback,
  failSummaryStream,
} from '../../../reducers/createDefectAssistantSlice'

const POLL_INTERVAL_MS = 700
const POLL_TIMEOUT_MS = 90_000

/**
 * Polls the streamed description one request at a time until it is whole. A stream that fails is
 * retried once as a blocking turn; if that fails too, the flow continues without a description.
 */
export const useSummaryStream = (
  advanceQuestionFlow: (arg: { blockingSummary: boolean }) => void
): void => {
  const dispatch = useTypedDispatch()
  const { finalizationId, status, fallbackAttempted } = useTypedSelector(
    (state) => state.createDefectAssistant.summary
  )

  useEffect(() => {
    if (!finalizationId || status !== 'streaming') return

    const startedAt = Date.now()
    let cancelled = false
    let timer: ReturnType<typeof setTimeout>

    const poll = async (): Promise<void> => {
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        dispatch(failSummaryStream())
        return
      }
      await dispatch(pollSummaryAction({ finalizationId }))
      if (cancelled) return
      timer = setTimeout(() => void poll(), POLL_INTERVAL_MS)
    }

    void poll()
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [dispatch, finalizationId, status])

  useEffect(() => {
    if (status !== 'failed' || fallbackAttempted) return
    dispatch(beginSummaryFallback())
    advanceQuestionFlow({ blockingSummary: true })
  }, [advanceQuestionFlow, dispatch, fallbackAttempted, status])
}
